import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownIcon, ArrowUpIcon, ArrowUturnLeftIcon, CheckCircleIcon, ChevronDownIcon, ChevronRightIcon, EyeIcon, EyeSlashIcon,
  FlagIcon, InformationCircleIcon, LockClosedIcon, PlusIcon, TrashIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import preopService from '../../services/preopService';
import { errText } from '../../services/scheduleService';
import { Question, shown } from './PublicPreop';

// -----------------------------------------------------------------------------
// Pre-op questions — the administrator tailors what patients are asked.
//
// The page edits a draft. Publishing makes it the version new questionnaires are
// sent with; ones already sent keep the questions they were sent with. The
// server (preop/formStore.js) has the last word on what may change: built-in
// questions keep their answers because the screening rules read them.
// -----------------------------------------------------------------------------

const TYPE_LABELS = { yesno: 'Yes / no', choice: 'Choose one', number: 'Number', text: 'Free text', list: 'List' };
const LEVEL_STYLE = {
  alert: 'bg-red-50 text-red-700 border-red-200',
  caution: 'bg-amber-50 text-amber-800 border-amber-200',
  note: 'bg-slate-50 text-slate-600 border-slate-200',
};
const LEVEL_HELP = {
  alert: 'Alert — likely to change the plan',
  caution: 'Caution — worth checking',
  note: 'Note — for information',
};
const inputCls = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';
const labelCls = 'block text-xs font-semibold text-slate-600';

const rid = () => Math.random().toString(36).slice(2, 8);
const clone = (x) => JSON.parse(JSON.stringify(x));
const fmtWhen = (d) => (d ? new Date(d).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const yesNoValues = (q) => [['yes', 'Yes'], ['no', 'No'], ...(q.allowUnsure ? [['unsure', 'Not sure']] : [])];
const answerValues = (q) => (q.type === 'yesno' ? yesNoValues(q) : (q.options || []).map((o) => [o.value, o.label || o.value]));

// A fresh question of the practice's own.
const newQuestion = (type = 'yesno') => {
  const q = { id: `c_${rid()}${rid()}`, type, label: '' };
  if (type === 'choice') q.options = [{ value: `o_${rid()}`, label: '' }, { value: `o_${rid()}`, label: '' }];
  if (type === 'number') Object.assign(q, { min: 0, max: 100, unit: '' });
  if (type === 'list') q.fields = [{ id: 'f1', label: '' }];
  return q;
};
// Changing a custom question's type keeps the wording, drops what no longer fits.
const retype = (q, type) => {
  const { label, help, short, id, hidden, when, audience } = q;
  return { ...newQuestion(type), id, label, help, short, hidden, when, audience };
};

// Who sees what — mirrors backend form.js formFor.
const fits = (a, { sex, age }) => !a || ((!a.sex || a.sex === sex) && (a.minAge == null || age >= a.minAge) && (a.maxAge == null || age <= a.maxAge));
const formFor = (sections, who) => sections
  .filter((s) => !s.hidden && fits(s.audience, who))
  .map((s) => ({ ...s, questions: s.questions.filter((q) => !q.hidden && fits(q.audience, who)) }))
  .filter((s) => s.questions.length);

const Chip = ({ className = '', children, title }) => (
  <span title={title} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${className}`}>{children}</span>
);

const IconBtn = ({ onClick, title, disabled, children, danger }) => (
  <button type="button" onClick={onClick} title={title} aria-label={title} disabled={disabled}
    className={`rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent ${danger ? 'hover:text-red-600' : 'hover:text-slate-700'}`}>
    {children}
  </button>
);

const Field = ({ label, hint, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className={labelCls}>{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
  </label>
);

// -----------------------------------------------------------------------------
// One question's editor
// -----------------------------------------------------------------------------
const QuestionEditor = ({ q, builtin, flagInput, earlier, sections, sectionIndex, onChange, onMove }) => {
  const set = (patch) => onChange({ ...q, ...patch });
  const locked = !!builtin;
  const whenCtl = q.when ? earlier.find((e) => e.id === q.when.q) : null;

  const setOption = (i, patch) => set({ options: q.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
  const setField = (i, patch) => set({ fields: q.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  const toggleIn = (list = [], v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  return (
    <div className="space-y-4 border-t border-slate-100 bg-slate-50/60 px-4 py-4">
      {locked && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
          <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Built-in question. You can change the wording, move it or hide it. Its answers can't change
            {flagInput ? <> because the screening rules use them — <b>{flagInput}</b>.</> : '.'}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field label="Question the patient sees" className="md:col-span-2">
          <textarea rows={2} value={q.label || ''} onChange={(e) => set({ label: e.target.value })} className={`${inputCls} resize-none`} maxLength={300} placeholder="e.g. Have you had COVID-19 in the past six weeks?" />
        </Field>
        <Field label="Answer type" hint={locked ? 'Fixed for built-in questions' : null}>
          <select value={q.type} disabled={locked} onChange={(e) => onChange(retype(q, e.target.value))} className={`${inputCls} disabled:bg-slate-100`}>
            {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Help text (optional)" className="md:col-span-2">
          <input value={q.help || ''} onChange={(e) => set({ help: e.target.value })} className={inputCls} maxLength={300} placeholder="Shown under the question in smaller type" />
        </Field>
        <Field label="Short label for the doctor" hint="How the answer is listed for the anaesthetist">
          <input value={q.short || ''} onChange={(e) => set({ short: e.target.value })} className={inputCls} maxLength={120} placeholder={(q.label || '').slice(0, 60)} />
        </Field>
      </div>

      {/* Type-specific */}
      {q.type === 'yesno' && !locked && (
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!q.allowUnsure} onChange={(e) => set({ allowUnsure: e.target.checked })} /> Offer “Not sure”</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!q.optional} onChange={(e) => set({ optional: e.target.checked })} /> Optional</label>
        </div>
      )}

      {q.type === 'choice' && (
        <div>
          <span className={labelCls}>Answers {locked && <span className="font-normal text-slate-400">(wording only)</span>}</span>
          <div className="mt-1 space-y-2">
            {q.options.map((o, i) => (
              <div key={o.value} className="flex items-center gap-2">
                <input value={o.label || ''} onChange={(e) => setOption(i, { label: e.target.value })} className={inputCls} maxLength={150} placeholder={`Answer ${i + 1}`} />
                {!locked && (
                  <>
                    <IconBtn title="Up" disabled={i === 0} onClick={() => { const o2 = [...q.options]; [o2[i - 1], o2[i]] = [o2[i], o2[i - 1]]; set({ options: o2 }); }}><ArrowUpIcon className="h-4 w-4" /></IconBtn>
                    <IconBtn title="Remove answer" danger disabled={q.options.length <= 2} onClick={() => set({ options: q.options.filter((_, j) => j !== i) })}><TrashIcon className="h-4 w-4" /></IconBtn>
                  </>
                )}
              </div>
            ))}
          </div>
          {!locked && q.options.length < 12 && (
            <button type="button" onClick={() => set({ options: [...q.options, { value: `o_${rid()}`, label: '' }] })} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand-teal">
              <PlusIcon className="h-4 w-4" /> Add an answer
            </button>
          )}
          {!locked && <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={!!q.optional} onChange={(e) => set({ optional: e.target.checked })} /> Optional</label>}
        </div>
      )}

      {(q.type === 'yesno' || q.type === 'choice') && (
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          {!locked && (
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input type="checkbox" checked={q.detailLabel != null} onChange={(e) => set(e.target.checked ? { detailLabel: 'Tell us more', detailOn: q.type === 'choice' ? [] : undefined } : { detailLabel: undefined, detailPlaceholder: undefined, detailOn: undefined })} />
              Ask “tell us more” after some answers
            </label>
          )}
          {(q.detailLabel != null || (locked && q.detailLabel)) && (
            <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="“Tell us more” prompt"><input value={q.detailLabel || ''} onChange={(e) => set({ detailLabel: e.target.value })} className={inputCls} maxLength={120} /></Field>
              <Field label="Example text in the box (optional)"><input value={q.detailPlaceholder || ''} onChange={(e) => set({ detailPlaceholder: e.target.value })} className={inputCls} maxLength={120} /></Field>
              {q.type === 'choice' && !locked && (
                <div className="md:col-span-2">
                  <span className={labelCls}>Ask after</span>
                  <div className="mt-1 flex flex-wrap gap-3 text-sm">
                    {q.options.map((o) => (
                      <label key={o.value} className="flex items-center gap-1.5"><input type="checkbox" checked={(q.detailOn || []).includes(o.value)} onChange={() => set({ detailOn: toggleIn(q.detailOn, o.value) })} /> {o.label || '(no wording yet)'}</label>
                    ))}
                  </div>
                </div>
              )}
              {q.type === 'yesno' && <p className="text-xs text-slate-400 md:col-span-2">Asked after “Yes”.</p>}
            </div>
          )}
          {locked && !q.detailLabel && <p className="text-xs text-slate-400">No “tell us more” box on this question.</p>}
        </div>
      )}

      {q.type === 'number' && (
        <div className="grid grid-cols-3 gap-3">
          <Field label="Lowest allowed"><input type="number" value={q.min ?? ''} disabled={locked} onChange={(e) => set({ min: e.target.value })} className={`${inputCls} disabled:bg-slate-100`} /></Field>
          <Field label="Highest allowed"><input type="number" value={q.max ?? ''} disabled={locked} onChange={(e) => set({ max: e.target.value })} className={`${inputCls} disabled:bg-slate-100`} /></Field>
          <Field label="Unit"><input value={q.unit || ''} disabled={locked} onChange={(e) => set({ unit: e.target.value })} className={`${inputCls} disabled:bg-slate-100`} maxLength={15} placeholder="e.g. kg" /></Field>
        </div>
      )}

      {q.type === 'text' && (
        <Field label="Example text in the box (optional)"><input value={q.placeholder || ''} onChange={(e) => set({ placeholder: e.target.value })} className={inputCls} maxLength={120} /></Field>
      )}

      {q.type === 'list' && (
        <div>
          <span className={labelCls}>Columns {locked && <span className="font-normal text-slate-400">(wording only)</span>}</span>
          <div className="mt-1 space-y-2">
            {q.fields.map((f, i) => (
              <div key={f.id} className="flex items-center gap-2">
                <input value={f.label || ''} onChange={(e) => setField(i, { label: e.target.value })} className={inputCls} maxLength={60} placeholder={`Column ${i + 1} name`} />
                <input value={f.placeholder || ''} onChange={(e) => setField(i, { placeholder: e.target.value })} className={inputCls} maxLength={80} placeholder="Example (optional)" />
                {!locked && <IconBtn title="Remove column" danger disabled={q.fields.length <= 1} onClick={() => set({ fields: q.fields.filter((_, j) => j !== i) })}><TrashIcon className="h-4 w-4" /></IconBtn>}
              </div>
            ))}
          </div>
          {!locked && q.fields.length < 3 && (
            <button type="button" onClick={() => set({ fields: [...q.fields, { id: `f${q.fields.length + 1}_${rid()}`.slice(0, 20), label: '' }] })} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand-teal">
              <PlusIcon className="h-4 w-4" /> Add a column
            </button>
          )}
          <Field label="“Add another” button" className="mt-2"><input value={q.addLabel || ''} onChange={(e) => set({ addLabel: e.target.value })} className={inputCls} maxLength={60} placeholder="Add another" /></Field>
        </div>
      )}

      {!locked && ['number', 'text', 'list'].includes(q.type) && (
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!q.required} onChange={(e) => set({ required: e.target.checked })} /> The patient must answer this</label>
      )}

      {/* Conditions */}
      {!locked && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <span className={labelCls}>Only ask if…</span>
            <select
              value={q.when?.q || ''}
              onChange={(e) => set({ when: e.target.value ? { q: e.target.value, in: [] } : undefined })}
              className={`mt-1 ${inputCls}`}
            >
              <option value="">Always ask</option>
              {earlier.filter((e) => e.type === 'yesno' || e.type === 'choice').map((e) => (
                <option key={e.id} value={e.id}>{(e.short || e.label || '').slice(0, 70)}</option>
              ))}
            </select>
            {whenCtl && (
              <div className="mt-2 flex flex-wrap gap-3 text-sm">
                <span className="text-xs text-slate-500">…the answer is</span>
                {answerValues(whenCtl).map(([v, l]) => (
                  <label key={v} className="flex items-center gap-1.5"><input type="checkbox" checked={q.when.in.includes(v)} onChange={() => set({ when: { ...q.when, in: toggleIn(q.when.in, v) } })} /> {l}</label>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <span className={labelCls}>Who is asked</span>
            <div className="mt-1 grid grid-cols-3 gap-2">
              <select value={q.audience?.sex || ''} onChange={(e) => set({ audience: { ...q.audience, sex: e.target.value || undefined } })} className={inputCls}>
                <option value="">Everyone</option><option value="F">Female</option><option value="M">Male</option>
              </select>
              <input type="number" min={0} max={120} value={q.audience?.minAge ?? ''} onChange={(e) => set({ audience: { ...q.audience, minAge: e.target.value === '' ? undefined : e.target.value } })} className={inputCls} placeholder="From age" />
              <input type="number" min={0} max={120} value={q.audience?.maxAge ?? ''} onChange={(e) => set({ audience: { ...q.audience, maxAge: e.target.value === '' ? undefined : e.target.value } })} className={inputCls} placeholder="To age" />
            </div>
          </div>
        </div>
      )}

      {/* Flag for the anaesthetist */}
      {!locked && (
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <span className={`${labelCls} flex items-center gap-1`}><FlagIcon className="h-4 w-4" /> Flag for the anaesthetist</span>
          <div className="mt-1 grid grid-cols-1 gap-3 md:grid-cols-3">
            <select value={q.flag?.level || ''} onChange={(e) => set({ flag: e.target.value ? { on: [], ...q.flag, level: e.target.value, message: q.flag?.message || q.short || q.label || '' } : undefined })} className={inputCls}>
              <option value="">No flag</option>
              {Object.entries(LEVEL_HELP).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {q.flag && (
              <input value={q.flag.message || ''} onChange={(e) => set({ flag: { ...q.flag, message: e.target.value } })} className={`${inputCls} md:col-span-2`} maxLength={200} placeholder="What the anaesthetist sees, e.g. Recent COVID-19 infection" />
            )}
          </div>
          {q.flag && (q.type === 'yesno' || q.type === 'choice') && (
            <div className="mt-2 flex flex-wrap gap-3 text-sm">
              <span className="text-xs text-slate-500">Raise it when the answer is</span>
              {answerValues(q).map(([v, l]) => (
                <label key={v} className="flex items-center gap-1.5"><input type="checkbox" checked={(q.flag.on || []).includes(v)} onChange={() => set({ flag: { ...q.flag, on: toggleIn(q.flag.on, v) } })} /> {l || '(no wording yet)'}</label>
              ))}
            </div>
          )}
          {q.flag && q.type === 'number' && (
            <div className="mt-2 grid max-w-md grid-cols-2 gap-3">
              <Field label="Flag if above"><input type="number" value={q.flag.above ?? ''} onChange={(e) => set({ flag: { ...q.flag, above: e.target.value === '' ? undefined : e.target.value } })} className={inputCls} /></Field>
              <Field label="Flag if below"><input type="number" value={q.flag.below ?? ''} onChange={(e) => set({ flag: { ...q.flag, below: e.target.value === '' ? undefined : e.target.value } })} className={inputCls} /></Field>
            </div>
          )}
          {q.flag && (q.type === 'text' || q.type === 'list') && <p className="mt-2 text-xs text-slate-400">Raised whenever the patient writes something, with what they wrote.</p>}
        </div>
      )}

      <div className="flex items-center gap-2 text-sm">
        <span className="text-xs font-semibold text-slate-600">Move to section</span>
        <select value={sectionIndex} onChange={(e) => onMove(Number(e.target.value))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
          {sections.map((s, i) => <option key={s.id} value={i}>{s.title || `Section ${i + 1}`}</option>)}
        </select>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Preview as the patient
// -----------------------------------------------------------------------------
const Preview = ({ sections, onClose }) => {
  const [who, setWho] = useState({ sex: 'F', age: 45 });
  const [answers, setAnswers] = useState({});
  const form = useMemo(() => formFor(sections, who), [sections, who]);
  const count = form.reduce((n, s) => n + s.questions.length, 0);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="flex h-full w-full max-w-2xl flex-col bg-slate-50 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-3">
          <div className="flex-1">
            <h2 className="font-bold text-brand-deep">Preview — what a patient sees</h2>
            <p className="text-xs text-slate-500">{form.length} sections · {count} questions (before any “only ask if”). Nothing here is saved.</p>
          </div>
          <select value={who.sex} onChange={(e) => setWho({ ...who, sex: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
            <option value="F">Female</option><option value="M">Male</option>
          </select>
          <label className="flex items-center gap-1 text-sm text-slate-600">age
            <input type="number" min={0} max={110} value={who.age} onChange={(e) => setWho({ ...who, age: Number(e.target.value) || 0 })} className="w-16 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
          </label>
          <IconBtn title="Close" onClick={onClose}><XMarkIcon className="h-5 w-5" /></IconBtn>
        </div>
        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          {form.map((s, i) => (
            <section key={s.id} className="space-y-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Step {i + 1} of {form.length}</div>
                <h3 className="text-lg font-bold text-brand-deep">{s.title}</h3>
                {s.intro && <p className="mt-1 text-sm text-slate-600">{s.intro}</p>}
              </div>
              {s.questions.filter((q) => shown(q, answers)).map((q) => (
                <Question key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswers((a) => { const n = { ...a }; if (v === undefined) delete n[q.id]; else n[q.id] = v; return n; })} />
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// The page
// -----------------------------------------------------------------------------
const PreopQuestions = () => {
  const [state, setState] = useState(null); // from GET /preop/form
  const [sections, setSections] = useState(null); // the working copy
  const [dirty, setDirty] = useState(false);
  const [sel, setSel] = useState(0);
  const [open, setOpen] = useState(null); // question id being edited
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [notice, setNotice] = useState('');
  const [preview, setPreview] = useState(false);
  const [publishing, setPublishing] = useState(null); // { note }
  const [historyOpen, setHistoryOpen] = useState(false);

  const adopt = useCallback((s, { keepSel = false } = {}) => {
    setState(s);
    setSections(clone(s.draft?.sections || s.current.sections));
    setDirty(false);
    setErrors([]);
    if (!keepSel) { setSel(0); setOpen(null); }
  }, []);

  useEffect(() => {
    preopService.getForm().then((s) => adopt(s)).catch((e) => setError(errText(e)));
  }, [adopt]);

  // Leaving with unsaved edits.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(''), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const builtinQ = useMemo(() => new Map((state?.defaults || []).flatMap((s) => s.questions.map((q) => [q.id, q]))), [state]);
  const builtinS = useMemo(() => new Set((state?.defaults || []).map((s) => s.id)), [state]);

  const edit = (fn) => {
    setSections((prev) => { const next = clone(prev); fn(next); return next; });
    setDirty(true);
  };

  const run = async (what, fn, message) => {
    setBusy(what);
    setError('');
    setErrors([]);
    try {
      const s = await fn();
      adopt(s, { keepSel: true });
      setWarnings(s.warnings || []);
      if (message) setNotice(typeof message === 'function' ? message(s) : message);
      return true;
    } catch (e) {
      const d = e.response?.data;
      if (d?.errors) { setErrors(d.errors); setWarnings(d.warnings || []); } else setError(errText(e));
      return false;
    } finally {
      setBusy('');
    }
  };

  const save = () => run('save', () => preopService.saveDraft(sections), 'Draft saved — patients still get the published questions until you publish.');
  const discard = () => {
    if (!window.confirm('Throw away the draft and go back to the published questions?')) return;
    run('discard', () => preopService.discardDraft(), 'Draft discarded.');
  };
  const publish = async () => {
    if (dirty && !(await run('save', () => preopService.saveDraft(sections)))) { setPublishing(null); return; }
    const note = publishing?.note;
    setPublishing(null);
    run('publish', () => preopService.publishForm(note), (s) => `Version ${s.published} published — new questionnaires use it from now on.`);
  };
  const resetToBuiltin = () => {
    if (!window.confirm('Replace the draft with the built-in questions? Your own questions will be removed from the draft (published versions are kept).')) return;
    setSections(clone(state.defaults));
    setDirty(true);
    setSel(0);
    setOpen(null);
  };

  if (!sections) {
    return (
      <div className="mx-auto max-w-7xl p-8">
        <h1 className="text-2xl font-bold text-brand-deep">Pre-op questions</h1>
        {error ? <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : <p className="mt-4 text-slate-400">Loading…</p>}
      </div>
    );
  }

  const section = sections[Math.min(sel, sections.length - 1)];
  const si = sections.indexOf(section);
  const hasDraft = !!state.draft || dirty;
  const current = state.versions.find((v) => v.version === state.current.version);
  const earlierOf = (qid) => {
    const out = [];
    for (const s of sections) for (const q of s.questions) { if (q.id === qid) return out; out.push(q); }
    return out;
  };
  const shownCount = (s) => s.questions.filter((q) => !q.hidden).length;

  const moveSection = (i, d) => {
    edit((ss) => { const [x] = ss.splice(i, 1); ss.splice(i + d, 0, x); });
    if (i === si) setSel(i + d);
    else if (i + d === si) setSel(si - d);
  };
  const moveQuestion = (qi, d) => edit((ss) => { const qs = ss[si].questions; const [x] = qs.splice(qi, 1); qs.splice(qi + d, 0, x); });
  const setQuestion = (qi, q) => edit((ss) => { ss[si].questions[qi] = q; });
  const toSection = (qi, target) => {
    if (target === si) return;
    edit((ss) => { const [x] = ss[si].questions.splice(qi, 1); ss[target].questions.push(x); });
    setSel(target);
  };
  const addQuestion = () => {
    const q = newQuestion();
    edit((ss) => { ss[si].questions.push(q); });
    setOpen(q.id);
  };
  const addSection = () => {
    const s = { id: `s_${rid()}${rid()}`, title: 'New section', questions: [] };
    edit((ss) => { ss.push(s); });
    setSel(sections.length);
  };

  return (
    <div className="mx-auto max-w-7xl p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-deep">Pre-op questions</h1>
          <p className="mt-1 text-slate-500">
            What patients are asked before their anaesthetic. Patients are sent <b>version {state.current.version}</b>
            {current?.published_at ? <> — published {fmtWhen(current.published_at)}{current.published_by_name ? ` by ${current.published_by_name}` : ''}</> : ' — the built-in questions'}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setPreview(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <EyeIcon className="h-4 w-4" /> Preview
          </button>
          <button onClick={save} disabled={!dirty || !!busy} className="rounded-lg border border-brand-navy bg-white px-3 py-2 text-sm font-semibold text-brand-navy hover:bg-blue-50 disabled:opacity-40">
            {busy === 'save' ? 'Saving…' : 'Save draft'}
          </button>
          <button onClick={() => setPublishing({ note: '' })} disabled={!hasDraft || !!busy} className="rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
            {busy === 'publish' ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>

      {hasDraft && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          <InformationCircleIcon className="h-5 w-5 shrink-0" />
          <span className="flex-1">
            {dirty ? 'You have unsaved changes.' : `Draft saved ${fmtWhen(state.draft.updated_at)}${state.draft.updated_by_name ? ` by ${state.draft.updated_by_name}` : ''}.`}
            {' '}Patients keep getting version {state.current.version} until you publish.
          </span>
          {state.draft && <button onClick={discard} disabled={!!busy} className="font-semibold text-amber-900 underline">Discard draft</button>}
          {dirty && !state.draft && <button onClick={() => adopt(state)} className="font-semibold text-amber-900 underline">Undo changes</button>}
        </div>
      )}

      {notice && <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-sm text-green-800"><CheckCircleIcon className="h-5 w-5" />{notice}</div>}
      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {errors.length > 0 && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <p className="font-semibold">Fix these first:</p>
          <ul className="mt-1 list-disc pl-5">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <ul className="list-disc pl-5">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[17rem_1fr]">
        {/* Sections */}
        <aside className="space-y-3">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-4 py-2 text-xs font-bold uppercase tracking-wide text-slate-400">Sections</div>
            {sections.map((s, i) => (
              <div key={s.id} className={`group flex items-center gap-1 border-b border-slate-100 pr-1 last:border-0 ${i === si ? 'bg-teal-50' : 'hover:bg-slate-50'}`}>
                <button onClick={() => { setSel(i); setOpen(null); }} className="min-w-0 flex-1 px-4 py-2.5 text-left">
                  <div className={`truncate text-sm font-semibold ${s.hidden ? 'text-slate-400 line-through' : i === si ? 'text-brand-deep' : 'text-slate-700'}`}>{s.title || 'Untitled'}</div>
                  <div className="text-xs text-slate-400">
                    {shownCount(s)} question{shownCount(s) === 1 ? '' : 's'}
                    {s.questions.length > shownCount(s) ? ` · ${s.questions.length - shownCount(s)} hidden` : ''}
                    {s.audience?.sex ? ` · ${s.audience.sex === 'F' ? 'women' : 'men'}` : ''}
                  </div>
                </button>
                <div className="flex flex-col opacity-0 transition group-hover:opacity-100">
                  <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20" title="Move up"><ArrowUpIcon className="h-3.5 w-3.5" /></button>
                  <button onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20" title="Move down"><ArrowDownIcon className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
            <button onClick={addSection} className="flex w-full items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-brand-teal hover:bg-slate-50">
              <PlusIcon className="h-4 w-4" /> Add a section
            </button>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white">
            <button onClick={() => setHistoryOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-semibold text-slate-700">
              Version history {historyOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
            </button>
            {historyOpen && (
              <ul className="divide-y divide-slate-100 border-t border-slate-100 text-xs">
                {state.versions.map((v) => (
                  <li key={v.version} className="px-4 py-2">
                    <div className="flex justify-between font-semibold text-slate-700">
                      <span>Version {v.version}{v.version === state.current.version ? ' · in use' : ''}</span>
                      <span className="font-normal text-slate-400">{v.used} sent</span>
                    </div>
                    <div className="text-slate-500">{v.note || (v.published_at ? 'No note' : '')}</div>
                    {v.published_at && <div className="text-slate-400">{fmtWhen(v.published_at)}{v.published_by_name ? ` · ${v.published_by_name}` : ''}</div>}
                  </li>
                ))}
              </ul>
            )}
            <button onClick={resetToBuiltin} className="flex w-full items-center gap-1.5 border-t border-slate-100 px-4 py-2.5 text-xs font-semibold text-slate-500 hover:text-slate-800">
              <ArrowUturnLeftIcon className="h-4 w-4" /> Start again from the built-in questions
            </button>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-500">
            <p className="font-semibold text-slate-700">How this works</p>
            <p className="mt-1">Built-in questions feed the screening flags (STOP-BANG, BMI, blood thinners, GLP-1…). You can reword, move or hide them, but not delete them or change their answers.</p>
            <p className="mt-1">Your own questions can raise their own flag for the anaesthetist.</p>
            <p className="mt-1">Questionnaires already sent keep the questions they were sent with.</p>
          </div>
        </aside>

        {/* Selected section */}
        <main className="min-w-0 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-start gap-3">
              <div className="grid min-w-0 flex-1 grid-cols-1 gap-3">
                <Field label="Section title"><input value={section.title || ''} onChange={(e) => edit((ss) => { ss[si].title = e.target.value; })} className={`${inputCls} text-base font-semibold`} maxLength={80} /></Field>
                <Field label="Introduction (optional)"><textarea rows={2} value={section.intro || ''} onChange={(e) => edit((ss) => { ss[si].intro = e.target.value; })} className={`${inputCls} resize-none`} maxLength={400} placeholder="A sentence shown at the top of this step" /></Field>
                {builtinS.has(section.id) ? (
                  section.audience && <p className="text-xs text-slate-500">Asked only of {section.audience.sex === 'F' ? 'women' : section.audience.sex === 'M' ? 'men' : 'patients'}{section.audience.minAge != null ? ` from ${section.audience.minAge}` : ''}{section.audience.maxAge != null ? ` to ${section.audience.maxAge}` : ''} (built in).</p>
                ) : (
                  <div className="grid max-w-md grid-cols-3 gap-2">
                    <Field label="Who is asked">
                      <select value={section.audience?.sex || ''} onChange={(e) => edit((ss) => { ss[si].audience = { ...ss[si].audience, sex: e.target.value || undefined }; })} className={inputCls}>
                        <option value="">Everyone</option><option value="F">Female</option><option value="M">Male</option>
                      </select>
                    </Field>
                    <Field label="From age"><input type="number" value={section.audience?.minAge ?? ''} onChange={(e) => edit((ss) => { ss[si].audience = { ...ss[si].audience, minAge: e.target.value === '' ? undefined : e.target.value }; })} className={inputCls} /></Field>
                    <Field label="To age"><input type="number" value={section.audience?.maxAge ?? ''} onChange={(e) => edit((ss) => { ss[si].audience = { ...ss[si].audience, maxAge: e.target.value === '' ? undefined : e.target.value }; })} className={inputCls} /></Field>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <button onClick={() => edit((ss) => { ss[si].hidden = !ss[si].hidden; })} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                  {section.hidden ? <><EyeIcon className="h-4 w-4" /> Show section</> : <><EyeSlashIcon className="h-4 w-4" /> Hide section</>}
                </button>
                {!builtinS.has(section.id) && (
                  <button
                    onClick={() => {
                      if (section.questions.some((q) => builtinQ.has(q.id))) { window.alert('Move the built-in questions to another section first — they can be hidden but not deleted.'); return; }
                      if (!window.confirm(`Delete the section “${section.title}” and its questions?`)) return;
                      edit((ss) => { ss.splice(si, 1); });
                      setSel(Math.max(0, si - 1));
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4" /> Delete section
                  </button>
                )}
              </div>
            </div>
            {section.hidden && <p className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600">This section is hidden — none of its questions are asked.</p>}
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            {section.questions.length === 0 && <p className="px-4 py-6 text-center text-sm text-slate-400">No questions in this section yet.</p>}
            {section.questions.map((q, qi) => {
              const builtin = builtinQ.get(q.id);
              const flagInput = builtin ? state.flag_inputs[q.id] : null;
              const isOpen = open === q.id;
              const ctl = q.when ? earlierOf(q.id).find((e) => e.id === q.when.q) : null;
              return (
                <div key={q.id} className="border-b border-slate-100 last:border-0">
                  <div className={`flex items-start gap-2 px-3 py-3 ${q.hidden ? 'bg-slate-50' : ''}`}>
                    <div className="flex flex-col">
                      <button onClick={() => moveQuestion(qi, -1)} disabled={qi === 0} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20" title="Move up"><ArrowUpIcon className="h-3.5 w-3.5" /></button>
                      <button onClick={() => moveQuestion(qi, 1)} disabled={qi === section.questions.length - 1} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20" title="Move down"><ArrowDownIcon className="h-3.5 w-3.5" /></button>
                    </div>
                    <button onClick={() => setOpen(isOpen ? null : q.id)} className="min-w-0 flex-1 text-left">
                      <div className={`text-sm font-semibold ${q.hidden ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{q.label || <span className="italic text-slate-400">New question — add the wording</span>}</div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        <Chip className="border-slate-200 bg-white text-slate-500">{TYPE_LABELS[q.type]}</Chip>
                        {builtin ? <Chip className="border-blue-200 bg-blue-50 text-blue-700" title={flagInput || 'Built-in question'}><LockClosedIcon className="h-3 w-3" /> Built-in{flagInput ? ' · feeds flags' : ''}</Chip> : <Chip className="border-teal-200 bg-teal-50 text-teal-700">Your question</Chip>}
                        {q.flag && <Chip className={LEVEL_STYLE[q.flag.level]}><FlagIcon className="h-3 w-3" /> {q.flag.level}</Chip>}
                        {ctl && <Chip className="border-slate-200 bg-white text-slate-500">only if “{(ctl.short || ctl.label || '').slice(0, 40)}”</Chip>}
                        {q.audience?.sex && <Chip className="border-slate-200 bg-white text-slate-500">{q.audience.sex === 'F' ? 'women' : 'men'}</Chip>}
                        {q.hidden && <Chip className="border-slate-300 bg-slate-100 text-slate-600">hidden</Chip>}
                      </div>
                    </button>
                    <IconBtn
                      title={q.hidden ? 'Ask this question' : 'Hide this question'}
                      onClick={() => {
                        if (!q.hidden && flagInput && !window.confirm(`This question feeds the screening rules (${flagInput}). Hide it anyway?`)) return;
                        setQuestion(qi, { ...q, hidden: !q.hidden || undefined });
                      }}
                    >
                      {q.hidden ? <EyeIcon className="h-4 w-4" /> : <EyeSlashIcon className="h-4 w-4" />}
                    </IconBtn>
                    {!builtin && (
                      <IconBtn title="Delete question" danger onClick={() => { if (window.confirm('Delete this question?')) edit((ss) => { ss[si].questions.splice(qi, 1); }); }}>
                        <TrashIcon className="h-4 w-4" />
                      </IconBtn>
                    )}
                    <IconBtn title={isOpen ? 'Close' : 'Edit'} onClick={() => setOpen(isOpen ? null : q.id)}>
                      {isOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                    </IconBtn>
                  </div>
                  {isOpen && (
                    <QuestionEditor
                      q={q}
                      builtin={builtin}
                      flagInput={flagInput}
                      earlier={earlierOf(q.id)}
                      sections={sections}
                      sectionIndex={si}
                      onChange={(next) => setQuestion(qi, next)}
                      onMove={(target) => toSection(qi, target)}
                    />
                  )}
                </div>
              );
            })}
            <button onClick={addQuestion} className="flex w-full items-center justify-center gap-1.5 border-t border-slate-100 px-4 py-3 text-sm font-semibold text-brand-teal hover:bg-slate-50">
              <PlusIcon className="h-4 w-4" /> Add a question to “{section.title || 'this section'}”
            </button>
          </div>
        </main>
      </div>

      {preview && <Preview sections={sections} onClose={() => setPreview(false)} />}

      {publishing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setPublishing(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-brand-deep">Publish these questions?</h2>
            <p className="mt-1 text-sm text-slate-600">
              New questionnaires will use them from now on. Questionnaires already sent — even ones the patient hasn't finished — keep the questions they were sent with.
            </p>
            <Field label="What changed (optional)" className="mt-3">
              <input autoFocus value={publishing.note} onChange={(e) => setPublishing({ note: e.target.value })} className={inputCls} maxLength={300} placeholder="e.g. Added COVID-19 question; reworded smoking" />
            </Field>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setPublishing(null)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700">Cancel</button>
              <button onClick={publish} className="rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white">Publish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreopQuestions;
